import { ShoppingBag, ListChecks, CreditCard, Receipt } from 'lucide-react';
import { PageTour } from '@/components/common/PageTour';

/**
 * 4-step "First sale" walkthrough for new branch managers / cashiers.
 * Triggered from the POS page-level info callout's "Show me around"
 * button. Steps cover the simplest happy-path:
 *   1. Pick a branch + start a session
 *   2. Add items from the menu grid
 *   3. Pick payment method + collect
 *   4. Review the receipt + close the day
 *
 * Props: open, onOpenChange. Optional onJumpToSale to highlight the
 * "New sale" button after finishing.
 */
const STEPS = [
  {
    icon: ShoppingBag,
    title: '1. Pick a branch and open a session',
    body: (
      <>
        Pick the branch you&apos;re cashiering at from the top selector. The first sale of the day automatically
        <strong> opens a cash session</strong> — you&apos;ll close it at end-of-day to reconcile the till. If your branch
        uses a cash drawer, the kick command fires automatically when you collect cash.
      </>
    ),
  },
  {
    icon: ListChecks,
    title: '2. Build the order',
    body: (
      <>
        Tap items from the menu grid to add them to the cart. Tap an item again to bump the quantity, or use
        <strong> long-press</strong> to edit notes / pick add-ons. Sizes and modifiers (e.g. &quot;extra hot&quot;) flow through to
        the kitchen ticket. Discounts apply per-line or to the whole order.
      </>
    ),
  },
  {
    icon: CreditCard,
    title: '3. Take payment',
    body: (
      <>
        Hit <strong>Pay</strong> and pick a method — Cash, Card, Bank Transfer, Credit, or split across multiple. Cash
        triggers the change calculator and (if connected) the cash drawer. Card auto-prompts for the terminal slip
        number so reconciliation is one-to-one with your statement.
      </>
    ),
  },
  {
    icon: Receipt,
    title: '4. Print, repeat, close',
    body: (
      <>
        The receipt prints (or emails / WhatsApps) automatically. The cart resets — you&apos;re ready for the next
        customer. At end-of-day, hit <strong>Close Session</strong> in the page header: the system asks for the
        physical cash count and flags any variance for review. The <strong>Today&apos;s Performance</strong> overlay shows
        live revenue, top items, and any &quot;loss items&quot; (sold below cost) before you head home.
      </>
    ),
  },
];

export function PosFirstSaleTour({ open, onOpenChange }) {
  return (
    <PageTour
      open={open}
      onOpenChange={onOpenChange}
      steps={STEPS}
      tourName="POS First Sale walkthrough"
      testidPrefix="pos-tour"
    />
  );
}
